import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  Lock,
  LogOut,
  ShieldCheck,
  BookOpen,
  CheckCircle2,
  KeyRound,
  Camera,
  AlertTriangle,
} from "lucide-react";
import MediaUploader from "@/components/lifted/MediaUploader";
import { useAuth } from "@/context/AuthContext";

// Phase 21.3 — ALWAYS call the API on the same origin the page was loaded
// from. iOS Safari fetch rejects cross-origin requests with the generic
// "Load failed" error when ITP blocks third-party cookies, when CORS
// pre-flight is denied, or when a service-worker intercepts. By using a
// relative URL we guarantee same-origin and never hit any of those.
const API_BASE = "";

// Phase 21.1 — every critical request must fail gracefully after a short
// timeout so the page can never hang on "Loading…" forever.
function withTimeout(promise, ms = 8000) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out")), ms),
    ),
  ]);
}

// Phase 21.3 — iOS Safari throws a TypeError with message "Load failed" on
// network-layer rejection. Surface a clear, actionable message to the user
// instead of the cryptic browser default.
function friendlyError(err) {
  const m = String(err?.message || err || "");
  if (/load failed|failed to fetch|networkerror|network request failed/i.test(m)) {
    return "We could not reach the server. Check your connection and try again.";
  }
  if (/timed out/i.test(m)) {
    return "The request took too long. Please try again.";
  }
  return m || "Something went wrong. Please try again.";
}

async function safeFetchJson(path, opts = {}, timeoutMs = 8000) {
  // Phase 21.3 — log the raw + normalized request for production diagnostics.
  // These end up in the browser console + any client telemetry sink.
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  try {
    const r = await withTimeout(
      fetch(url, { credentials: "include", ...opts }),
      timeoutMs,
    );
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      const err = new Error(body.detail || `Request failed (${r.status})`);
      err.status = r.status;
      err.body = body;
      throw err;
    }
    return r.json();
  } catch (e) {
    // re-throw with a friendly message attached but preserve original for logs
    const friendly = friendlyError(e);
    const wrapped = new Error(friendly);
    wrapped.original = e;
    wrapped.status = e.status;
    wrapped.body = e.body;
    throw wrapped;
  }
}

export default function Settings() {
  const navigate = useNavigate();
  // Phase 21.3 — refresh the global AuthContext after a successful redeem
  // so ProtectedRoute / sidebar / permission checks immediately see the
  // newly-granted role. Without this, navigating to `/affiliate` after
  // redeem hits the "Access denied" guard because AuthContext still holds
  // the pre-redeem role set.
  const { refreshMe } = useAuth();

  // Phase 21.1 — explicit auth-load state so the page renders a useful
  // error/fallback UI instead of an infinite spinner.
  const [me, setMe] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const [trainingState, setTrainingState] = useState(null);

  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwMsg, setPwMsg] = useState(null);
  const [pwSaving, setPwSaving] = useState(false);

  const [code, setCode] = useState("");
  const [codeMsg, setCodeMsg] = useState(null);
  const [codeSaving, setCodeSaving] = useState(false);

  const [avatarMedia, setAvatarMedia] = useState([]);
  const [avatarMsg, setAvatarMsg] = useState(null);
  const [avatarSaving, setAvatarSaving] = useState(false);

  // -------- Initial load --------
  useEffect(() => {
    let cancelled = false;
    async function loadMe() {
      setAuthLoading(true);
      setAuthError(null);
      try {
        const data = await safeFetchJson(`/api/auth/me`);
        if (!cancelled) setMe(data);
      } catch (e) {
        console.error("Settings: /auth/me failed", e);
        if (!cancelled) setAuthError(e.message || "Could not load your account.");
      } finally {
        if (!cancelled) setAuthLoading(false);
      }
      // Training is optional — never block render on it.
      try {
        const t = await safeFetchJson(`/api/training/overview`);
        if (!cancelled) setTrainingState(t);
      } catch {
        /* training:view not granted — that's fine */
      }
    }
    loadMe();
    return () => {
      cancelled = true;
    };
  }, []);

  // -------- Actions --------
  async function saveAvatar() {
    const url = avatarMedia[0]?.url;
    if (!url) {
      setAvatarMsg({ type: "err", text: "Upload an image first." });
      return;
    }
    setAvatarSaving(true);
    setAvatarMsg(null);
    try {
      const fresh = await safeFetchJson(`/api/auth/profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ avatar_url: url }),
      });
      setMe((m) => ({ ...(m || {}), ...fresh }));
      setAvatarMsg({ type: "ok", text: "Profile photo updated." });
      setAvatarMedia([]);
    } catch (e) {
      setAvatarMsg({ type: "err", text: e.message || "Could not save photo." });
    } finally {
      setAvatarSaving(false);
    }
  }

  async function changePassword(e) {
    e.preventDefault();
    setPwMsg(null);
    if (pw.next.length < 8) {
      setPwMsg({ type: "err", text: "New password must be at least 8 characters." });
      return;
    }
    if (pw.next !== pw.confirm) {
      setPwMsg({ type: "err", text: "Passwords do not match." });
      return;
    }
    setPwSaving(true);
    try {
      await safeFetchJson(`/api/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ current_password: pw.current, new_password: pw.next }),
      });
      setPwMsg({ type: "ok", text: "Password updated." });
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      setPwMsg({ type: "err", text: err.message || "Could not change password." });
    } finally {
      setPwSaving(false);
    }
  }

  async function logout() {
    try {
      await withTimeout(
        fetch(`/api/auth/logout`, { method: "POST", credentials: "include" }),
        5000,
      );
    } catch {
      /* still navigate home — local UI state is what the user cares about */
    }
    navigate("/");
  }

  async function redeemCode(e) {
    e.preventDefault();
    setCodeMsg(null);
    const raw = code;
    const trimmed = code.trim();
    // Phase 21.3 — defensive client-side normalize. Backend
    // `_normalize_access_code` strips case + non-alphanumerics; we mirror
    // the same collapse-whitespace+lowercase so console logs are easy to
    // correlate with backend logs.
    const normalized = trimmed.toLowerCase().replace(/\s+/g, " ");
    if (!trimmed) {
      setCodeMsg({ type: "err", text: "Enter an access code." });
      return;
    }
    setCodeSaving(true);
    console.info("[access-code] submit", { raw, trimmed, normalized });
    try {
      const body = await safeFetchJson(`/api/auth/redeem-access-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: trimmed }),
      });
      console.info("[access-code] success", body);

      if (body.already_assigned) {
        const lbl = body.label || body.role;
        setCodeMsg({ type: "ok", text: `You already have ${lbl} access.` });
        setCodeSaving(false);
        return;
      }

      // Phase 21.3 — explicit success messaging + auto-route to the matching
      // dashboard so the user immediately sees the unlocked surface and
      // doesn't have to find the new sidebar entry on their own.
      let copy;
      let nextRoute = null;
      if (body.access_type === "partner_affiliate") {
        copy = "Partner access code activated successfully. Loading your dashboard…";
        nextRoute = "/affiliate";
      } else if (body.access_type === "affiliate") {
        copy = "Access code activated successfully. Loading your affiliate dashboard…";
        nextRoute = "/affiliate";
      } else if (body.status === "pending") {
        copy = `${body.role} access requested — awaiting admin review.`;
      } else {
        copy = `${body.role} access granted.`;
      }
      setCodeMsg({ type: "ok", text: copy });
      setCode("");

      // Refresh /auth/me so role-aware UI updates without a manual reload.
      try {
        const fresh = await safeFetchJson(`/api/auth/me`);
        setMe(fresh);
        // Phase 21.3 — bubble the new role into the global AuthContext so
        // ProtectedRoute and the sidebar permission-gate immediately let
        // the user into the affiliate / partner dashboard.
        if (typeof refreshMe === "function") {
          try { await refreshMe(); } catch { /* non-fatal */ }
        }
        console.info("[access-code] refreshed user", {
          id: fresh.id,
          email: fresh.email,
          roles: fresh.roles,
          primary_role: fresh.primary_role,
        });
      } catch {
        /* dashboard will refresh on next nav; ignore */
      }
      if (nextRoute) {
        // small delay so the user reads the success toast before routing
        setTimeout(() => navigate(nextRoute), 900);
      }
    } catch (err) {
      console.error("[access-code] failed", err.original || err);
      // Phase 21.3 — explicit user-facing strings the user requested.
      let text;
      if (err.status === 400) {
        text = "Invalid access code. Please check the code and try again.";
      } else if (err.status === 401 || err.status === 403) {
        text = "Please sign in again to activate this code.";
      } else if (err.original) {
        text = "We could not activate this code right now. Please try again.";
      } else {
        text = err.message || "Invalid code";
      }
      setCodeMsg({ type: "err", text });
    } finally {
      setCodeSaving(false);
    }
  }

  // -------- Render --------
  // Phase 21.1 — we now ALWAYS render the page shell + access-code box. The
  // user can still activate their affiliate / partner code even when an
  // optional API call (training, profile refresh) is slow or fails. The
  // account/roles/training cards each handle their own loading + error state
  // independently so the page can never sit on a blank "Loading…" screen.
  const roles = me?.roles || [];
  const trainingRoles = [
    "affiliate",
    "driver",
    "partner",
    "franchisee",
    "trainer",
    "teacher",
    "training_admin",
    "admin",
    "master_admin",
    "secondary_admin",
  ];
  const hasTrainingAccess = roles.some((r) => trainingRoles.includes(r));

  return (
    <div className="p-6 lg:p-10 max-w-2xl" data-testid="settings-page">
      <div className="text-xs uppercase tracking-[0.2em] text-lifted-green mb-2">
        Your account
      </div>
      <h1 className="font-anton uppercase text-4xl text-white">Settings</h1>
      <p className="text-lifted-dim text-sm mt-2 mb-6">
        Manage how you sign in and review your role-based access across the Lifted ecosystem.
      </p>

      {/* -------- Phase 21.1 — non-blocking error/timeout fallback -------- */}
      {authError && (
        <section
          className="glass-panel p-5 mb-5 border border-amber-500/40"
          data-testid="settings-auth-fallback"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-300 mt-[2px]" />
            <div className="flex-1">
              <h2 className="font-bebas tracking-wider text-amber-200 text-lg uppercase">
                We&rsquo;re having trouble loading your account
              </h2>
              <p className="text-sm text-amber-100/80 mt-1">
                {authError}. You can still enter your access code below, or refresh the page.
              </p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="btn-lifted mt-3"
                data-testid="settings-reload-btn"
              >
                Refresh page
              </button>
            </div>
          </div>
        </section>
      )}

      {/* -------- Access code (ALWAYS visible — Phase 21.1) -------- */}
      <section className="glass-panel p-5 mb-5" data-testid="settings-access-code">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound size={16} className="text-lifted-green" />
          <h2 className="font-bebas tracking-wider text-white text-lg uppercase">
            Lifted Access Code
          </h2>
        </div>
        <p className="text-lifted-dim text-xs mb-3">
          Enter your access code to unlock the matching dashboard. Partner and franchisee codes enter pending review — admin approval activates the dashboard.
        </p>
        <form onSubmit={redeemCode} className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Enter your access code"
            className="flex-1 px-4 py-3 rounded-xl bg-black/40 border border-white/15 text-white focus:outline-none focus:border-lifted-green"
            data-testid="access-code-input"
            autoComplete="off"
          />
          <button
            type="submit"
            disabled={codeSaving || !code.trim()}
            className="btn-lifted disabled:opacity-50"
            data-testid="access-code-submit"
          >
            {codeSaving ? "Activating…" : "Activate Account"}
          </button>
        </form>
        {codeMsg && (
          <p
            className={`text-sm mt-3 ${
              codeMsg.type === "err" ? "text-red-300" : "text-lifted-green"
            }`}
            data-testid="access-code-msg"
          >
            {codeMsg.text}
          </p>
        )}
      </section>

      {/* -------- Profile photo -------- */}
      <section className="glass-panel p-5 mb-5" data-testid="settings-avatar">
        <div className="flex items-center gap-2 mb-3">
          <Camera size={16} className="text-lifted-green" />
          <h2 className="font-bebas tracking-wider text-white text-lg uppercase">
            Profile photo
          </h2>
        </div>
        <div className="flex flex-col sm:flex-row gap-5 items-start">
          <div className="shrink-0">
            <div className="h-24 w-24 rounded-full overflow-hidden border-2 border-lifted-green/40 bg-black grid place-items-center">
              {me?.avatar_url ? (
                <img
                  src={me.avatar_url}
                  alt=""
                  className="h-full w-full object-cover"
                  data-testid="settings-current-avatar"
                />
              ) : (
                <span className="font-anton text-3xl text-lifted-green">
                  {(me?.name || me?.email || "?")[0]?.toUpperCase()}
                </span>
              )}
            </div>
            <div className="text-[0.6rem] text-lifted-dim mt-2 text-center uppercase tracking-[0.18em]">
              Current photo
            </div>
          </div>
          <div className="flex-1 w-full">
            <MediaUploader
              value={avatarMedia}
              onChange={setAvatarMedia}
              purpose="profile_image"
              maxFiles={1}
              allowVideo={false}
              primaryEnabled={false}
              testId="settings-avatar-uploader"
            />
            {avatarMsg && (
              <div
                className={`text-xs mt-2 ${
                  avatarMsg.type === "ok" ? "text-lifted-green" : "text-rose-300"
                }`}
                data-testid="settings-avatar-msg"
              >
                {avatarMsg.text}
              </div>
            )}
            <button
              type="button"
              onClick={saveAvatar}
              disabled={avatarSaving || avatarMedia.length === 0 || authLoading || !me}
              className="btn-lifted mt-3 inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="settings-avatar-save"
            >
              {avatarSaving ? "Saving…" : "Save photo"}
            </button>
          </div>
        </div>
      </section>

      {/* -------- Account info -------- */}
      <section className="glass-panel p-5 mb-5" data-testid="settings-account-info">
        <div className="flex items-center gap-2 mb-3">
          <User size={16} className="text-lifted-green" />
          <h2 className="font-bebas tracking-wider text-white text-lg uppercase">
            Account
          </h2>
        </div>
        {authLoading ? (
          <div className="text-lifted-dim text-sm" data-testid="settings-account-loading">
            Loading account…
          </div>
        ) : !me ? (
          <div className="text-amber-200/80 text-sm" data-testid="settings-account-missing">
            Account details couldn&rsquo;t be loaded. You can still activate an access code above.
          </div>
        ) : (
          <div className="text-sm space-y-2">
            <Row label="Name" value={me.name} />
            <Row label="Email" value={me.email} mono />
            <Row label="User ID" value={me.id} mono small />
            <Row
              label="Account created"
              value={me.created_at?.slice(0, 19)?.replace("T", " ") || "—"}
            />
          </div>
        )}
      </section>

      {/* -------- Roles -------- */}
      <section className="glass-panel p-5 mb-5" data-testid="settings-roles">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={16} className="text-lifted-green" />
          <h2 className="font-bebas tracking-wider text-white text-lg uppercase">
            Roles
          </h2>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {authLoading ? (
            <span className="text-lifted-dim text-sm">Loading roles…</span>
          ) : roles.length === 0 ? (
            <span className="text-lifted-dim text-sm">No roles assigned.</span>
          ) : (
            roles.map((r) => (
              <span
                key={r}
                className="px-2.5 py-1 rounded-full text-xs uppercase tracking-wider bg-lifted-green/15 text-lifted-green border border-lifted-green/30 font-bebas"
                data-testid={`role-pill-${r}`}
              >
                {r}
              </span>
            ))
          )}
        </div>
        <div className="text-xs text-lifted-dim leading-relaxed">
          Role changes are made by a master admin. Reach out if you believe yours should change.
        </div>
      </section>

      {/* -------- Training -------- */}
      <section className="glass-panel p-5 mb-5" data-testid="settings-training-status">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen size={16} className="text-lifted-green" />
          <h2 className="font-bebas tracking-wider text-white text-lg uppercase">
            Lifted Training
          </h2>
        </div>
        {authLoading ? (
          <p className="text-lifted-dim text-sm">Checking training access…</p>
        ) : hasTrainingAccess ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 size={16} className="text-lifted-green" />
              <span className="text-white">You have access to training modules</span>
            </div>
            {trainingState?.tracks && trainingState.tracks.length > 0 && (
              <p className="text-xs text-lifted-dim">
                Tracks visible to you:{" "}
                <span className="text-white">
                  {trainingState.tracks.map((t) => t.key).join(", ")}
                </span>
              </p>
            )}
            <a
              href="/training"
              className="inline-block mt-2 text-lifted-green underline text-sm"
              data-testid="settings-training-link"
            >
              Open training hub →
            </a>
          </div>
        ) : (
          <p className="text-lifted-dim text-sm">
            Training is unlocked once your role grants <code>training:view</code> permission.
            Affiliates, drivers, partner stores, franchisees, trainers, and admins have access.
          </p>
        )}
      </section>

      {/* -------- Change password -------- */}
      <section className="glass-panel p-5 mb-5" data-testid="settings-change-password">
        <div className="flex items-center gap-2 mb-3">
          <Lock size={16} className="text-lifted-green" />
          <h2 className="font-bebas tracking-wider text-white text-lg uppercase">
            Change password
          </h2>
        </div>
        <form onSubmit={changePassword} className="space-y-3">
          <PwField
            label="Current password"
            value={pw.current}
            onChange={(v) => setPw((p) => ({ ...p, current: v }))}
            testId="pw-current"
          />
          <PwField
            label="New password"
            value={pw.next}
            onChange={(v) => setPw((p) => ({ ...p, next: v }))}
            testId="pw-next"
            helper="At least 8 characters."
          />
          <PwField
            label="Confirm new password"
            value={pw.confirm}
            onChange={(v) => setPw((p) => ({ ...p, confirm: v }))}
            testId="pw-confirm"
          />
          {pwMsg && (
            <p
              className={`text-sm ${
                pwMsg.type === "ok" ? "text-lifted-green" : "text-red-300"
              }`}
              data-testid="pw-message"
            >
              {pwMsg.text}
            </p>
          )}
          <button
            type="submit"
            disabled={pwSaving || authLoading || !me}
            className="btn-lifted disabled:opacity-50"
            data-testid="pw-submit"
          >
            {pwSaving ? "Saving…" : "Update password"}
          </button>
        </form>
      </section>

      {/* -------- Logout -------- */}
      <section className="glass-panel p-5" data-testid="settings-logout-section">
        <div className="flex items-center gap-2 mb-3">
          <LogOut size={16} className="text-lifted-green" />
          <h2 className="font-bebas tracking-wider text-white text-lg uppercase">
            Sign out
          </h2>
        </div>
        <p className="text-lifted-dim text-sm mb-3">
          Sign out on this device. You can sign back in any time.
        </p>
        <button
          onClick={logout}
          className="btn-lifted"
          data-testid="settings-logout"
        >
          Log out
        </button>
      </section>
    </div>
  );
}

function Row({ label, value, mono, small }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-xs uppercase tracking-wider text-lifted-dim">
        {label}
      </span>
      <span
        className={`${mono ? "font-mono" : ""} ${
          small ? "text-xs" : "text-sm"
        } text-white truncate max-w-[70%]`}
      >
        {value || "—"}
      </span>
    </div>
  );
}

function PwField({ label, value, onChange, testId, helper }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-lifted-dim mb-1">
        {label}
      </label>
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="new-password"
        className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/15 text-white focus:outline-none focus:border-lifted-green"
        data-testid={testId}
      />
      {helper && <p className="text-[10px] text-lifted-dim mt-1">{helper}</p>}
    </div>
  );
}
